import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Megaphone, GraduationCap, TrendingUp, ArrowRight, Palette, Rocket, Briefcase, Compass, ShieldCheck } from 'lucide-react';

// import Scoreboard from '../components/Scoreboard'; // iter558 · removed — ESPN scores can't ship without a license
import NewsletterSignup from '../components/NewsletterSignup';
import AthleteSpotlight from '../components/AthleteSpotlight';
import AdSlot from '../components/AdSlot';
import FlytStudiosTeaser from '../components/FlytStudiosTeaser';
import FeaturedReel from '../components/FeaturedReel';
// iter862 · WelcomeSpotlightTour is no longer imported/mounted here at
// all — it's mounted globally in App.js instead (see the comment
// below), since it now spans both Home and Profile.
import CaseStudiesGrid from '../components/CaseStudiesGrid';
import FlytTaglineHero from '../components/FlytTaglineHero';
import FlytFilmsCarousel from '../components/FlytFilmsCarousel';
import HomepageLiveActivity from '../components/HomepageLiveActivity';
import { prefetchStory } from '../utils/prefetch';
import PersonalRows from '../components/PersonalRows';
import HorizontalRow from '../components/HorizontalRow';
// iter226 · MissionBanner render + import removed from Homepage.
// The component file itself is kept in the tree in case another
// surface wants to reuse it in the future.
import StatusBadge from '../components/StatusBadge';
import { ComingSoonSection } from '../components/CollectionsAndComing';
import CollectionsRow from '../components/CollectionsRow';
import CardSaveOverlay from '../components/CardSaveOverlay';
import SaveButton from '../components/SaveButton';
import { resolveImageUrl } from '../components/ImageUpload';
import { useChannel } from '../contexts/ChannelContext';
import { useUserAuth } from '../contexts/UserAuthContext';
import {
  PersonaFocusBanner,
  PersonaCTAStrip,
  usePersonaTailoring,
} from '../components/PersonaTailoring';
import PersonaActionPanel from '../components/PersonaActionPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tiny inline component used by the "Stories From" stat tile. Renders
// a 24px team logo and falls back to an initials chip if the upstream
// (Wikimedia Commons) image fails to load — that way the tile never goes
// blank if a logo URL changes upstream.
const ProgramLogo = ({ logo }) => {
  const [errored, setErrored] = useState(false);
  if (errored || !logo.src) {
    return (
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-black/85 text-white text-[8px] font-extrabold tracking-wider"
        title={logo.alt}
        aria-label={logo.alt}
      >
        {logo.fallback || logo.alt.slice(0, 3).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      src={logo.src}
      alt={logo.alt}
      title={logo.alt}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      className="w-7 h-7 object-contain"
    />
  );
};

const Homepage = ({ compact = false, networkHeroSlot = null, hideDiscovery = false }) => {
  const channel = useChannel();
  const isFlyt = channel.id === 'flyt';
  const isVisionary = channel.id === 'dreams_visionary';
  const accent = channel.accentClass;

  // iter219 · Sign-in awareness for the network home. Logged-out
  // visitors see a curated subset (hero → landing block → keep the
  // essentials → breaking news at the bottom) so the page teaches
  // instead of overwhelming. Signed-in users see the full homepage,
  // exactly as before, minus the removed AudienceHero.
  const { user } = useUserAuth();
  const isLoggedOut = !user;

  // Persona tailoring — affects story ordering, audience cards, and what
  // top-of-page CTA the user sees. Falls back gracefully when no persona.
  const { filterStories, filterAudienceCards } = usePersonaTailoring();

  const [featuredStories, setFeaturedStories] = useState([]);
  const [latestStories, setLatestStories] = useState([]);
  const [breakingStories, setBreakingStories] = useState([]);
  const [rotationStories, setRotationStories] = useState([]);
  const [rotationIndex, setRotationIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // iter509 · Refetch stories on mount AND every time the app comes
  // back to the foreground (visibilitychange fires on both web tab
  // switches and Capacitor iOS resume). Without this, TestFlight
  // users see whatever the app cached at first launch until they
  // fully kill + reopen — which is why new stories appear instantly
  // on web but seem stuck on iOS. `visibilitychange` also covers
  // the "app has been backgrounded for a while" case.
  const fetchStories = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = `&channel=${channel.id}`;
      const [featured, latest, breaking, rotation] = await Promise.all([
        axios.get(`${API}/stories?featured=true&limit=5${params}`),
        axios.get(`${API}/stories?limit=9${params}`),
        axios.get(`${API}/stories?breaking=true&limit=3${params}`),
        axios.get(`${API}/stories?hero_rotation=true&limit=6${params}`)
      ]);
      setFeaturedStories(Array.isArray(featured.data) ? featured.data : []);
      setLatestStories(Array.isArray(latest.data) ? latest.data : []);
      setBreakingStories(Array.isArray(breaking.data) ? breaking.data : []);
      setRotationStories(Array.isArray(rotation.data) ? rotation.data : []);
      setRotationIndex(0);
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [channel.id]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  // Refetch silently whenever the tab/app becomes visible again.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        fetchStories({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVis);
    // Some iOS Capacitor cases don't fire visibilitychange reliably —
    // pageshow is a belt-and-suspenders backup for bfcache restores.
    window.addEventListener('pageshow', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pageshow', onVis);
    };
  }, [fetchStories]);

  // Auto-rotate hero feature every 7 seconds when there are 2+ candidates.
  useEffect(() => {
    if (rotationStories.length < 2) return undefined;
    const id = setInterval(() => {
      setRotationIndex((prev) => (prev + 1) % rotationStories.length);
    }, 7000);
    return () => clearInterval(id);
  }, [rotationStories.length]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const rotationFeature = rotationStories.length > 0 ? rotationStories[rotationIndex % rotationStories.length] : null;
  const heroFeature = rotationFeature || featuredStories[0] || latestStories[0];
  const sideStoriesPool = [...featuredStories.slice(rotationFeature ? 0 : 1), ...latestStories]
    .filter(s => s && (!heroFeature || s.id !== heroFeature.id));
  const seen = new Set();
  const dedupedSideStories = sideStoriesPool.filter(s => {
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
  // Persona-aware prioritization: matching categories surface first, the
  // rest still show below so the page never goes empty. Falls back to the
  // original ordering when there's no persona.
  const sideStories = filterStories(dedupedSideStories).slice(0, 4);
  const tailoredLatest = filterStories(latestStories);

  const audienceCardsRaw = isVisionary ? [
    { icon: Palette, title: 'For Artists', body: 'Painters, musicians, writers — share the work behind the work.', to: '/visionary/contact?type=Artist', cta: 'Get Featured', testid: 'platform-artists' },
    { icon: Briefcase, title: 'For CEOs', body: 'Founder stories no PR team would let you tell.', to: '/visionary/contact?type=CEO', cta: 'Tell Your Story', testid: 'platform-ceos' },
    { icon: Rocket, title: 'For Entrepreneurs', body: 'From garage to Series B — the unfiltered build.', to: '/visionary/contact?type=Entrepreneur', cta: 'Pitch In', testid: 'platform-entrepreneurs' },
    { icon: Compass, title: 'For Pioneers', body: 'Operators, inventors, world-changers — get the lens you deserve.', to: '/visionary/contact?type=Pioneer', cta: 'Start a Conversation', testid: 'platform-pioneers' },
  ] : [
    { icon: Trophy, title: 'For Athletes', body: 'High school to pro — share your real story.', to: '/athletes', cta: 'Apply Now', testid: 'platform-athletes' },
    { icon: ShieldCheck, title: 'For Recruiters', body: 'Discover, evaluate, and organize verified athletes.', to: '/recruiter/signup', cta: 'Apply for Access', testid: 'platform-recruiters' },
    { icon: Megaphone, title: 'For Brands', body: 'Match with athletes. Build the whole campaign.', to: '/brands', cta: 'Partner With Us', testid: 'platform-brands' },
    { icon: GraduationCap, title: 'For Programs', body: 'Schools, ADs, donors — give athletes a story.', to: '/programs', cta: 'For Programs', testid: 'platform-programs' },
    { icon: TrendingUp, title: 'For Investors', body: 'Building the infrastructure for athlete storytelling.', to: '/contact', cta: 'Start a Conversation', testid: 'platform-investors' },
  ];
  // Apply persona filter — when tailoring is active, this typically narrows
  // the grid down to the ONE card matching the user's persona. When no
  // persona is set, falls back to the full set unchanged.
  const audienceCards = filterAudienceCards(audienceCardsRaw);

  return (
    <div data-testid="homepage" className={compact ? 'homepage-compact-dark bg-black text-white' : ''}>
      {/* iter489 · The old site-wide 3-step WelcomeSpotlightTour was
          removed from here per Jay (2026-02) — it felt over-explained
          on the home feed and interrupted the primary "browse stories"
          flow. iter862 · WelcomeSpotlightTour has since been rebuilt
          into a full athlete-only welcome walkthrough (Launch Pad,
          Media Hub, FLYT ID, Profile) and is mounted globally in
          App.js instead, so it can span both Home and Profile — not
          mounted here anymore at all, on purpose. */}
      {/* Top-of-page persona tailoring signals — appear only for signed-in
          users with a persona set. Banner is the global indicator + opt-out,
          CTA strip surfaces the most relevant next action, and the action
          panel gives them their dedicated workspace (ads for fans, ad-upload
          for brands, mission docs for investors, etc.). */}
      <PersonaFocusBanner />
      <PersonaCTAStrip />
      <PersonaActionPanel />

      {/* 0. LIVE ACTIVITY HERO — Continue reading / Notifications prompt */}
      <HomepageLiveActivity />

      {/* iter558 · ESPN Scoreboard removed — App Store rejection risk.
          We don't have a license to redistribute live ESPN game data,
          so the banner is fully unmounted. Component + backend route
          left in place as dead code in case licensing lands later. */}

      {/* 2. BREAKING NEWS BAR — flips to black/white when stacked under NetworkPage.
             iter219 · Signed-out visitors see the breaking news bar at
             the very bottom of the page instead of at the top — it's
             valuable but not the first impression we want new users to
             absorb. See the mirror render at the end of this component.
             iter229h · Also hidden in `compact` mode because NetworkPage
             now renders its own breaking ticker directly ABOVE the
             Marcus Bell hero for signed-in visitors — showing both
             would double-render the same headlines on `/`. */}
      {breakingStories.length > 0 && !isLoggedOut && !compact && (
        <div className={`py-3 border-b ${compact ? 'bg-black border-white/10' : 'bg-white border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
              <span className={`font-display text-xs uppercase whitespace-nowrap tracking-wider ${compact ? 'text-red-500' : 'text-red-600'}`}>● Breaking</span>
              {breakingStories.map((story, idx) => (
                <Link
                  key={story.id}
                  to={`${channel.storiesPath}/${story.slug}`}
                  onMouseEnter={() => prefetchStory(story.slug)}
                  onTouchStart={() => prefetchStory(story.slug)}
                  className={`font-body text-sm whitespace-nowrap transition ${compact ? 'text-white hover:text-blue-300' : 'hover-gradient'}`}
                  data-testid={`breaking-story-${idx}`}
                >
                  {story.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 2.5 MISSION BANNER — Dreams League positioning, immediately after breaking.
          Hidden in `compact` mode (when Homepage is stacked under NetworkPage on `/`).
          Lives only on `/network` (Stories tab) per product direction.
          iter219 · Also hidden for signed-out users on any route — the
          `PublicHomeLandingBlock` on `/` owns the "why Flythouse"
          messaging for new visitors. */}
      {/* iter226 · Removed the "Home of Access to Athletes"
          MissionBanner entirely (both signed-in and signed-out).
          Per Jay it was cluttering the network home; the
          `PublicHomeLandingBlock` covers the "what is Flythouse"
          messaging for new visitors, and signed-in users don't
          need the marketing framing. */}

      {/* 3. FEATURED STORY + TOP STORIES (top of page) — League & Visionary only.
              FLYT gets its own tagline hero (see block below).
              iter229d · `compact` still shows Top Stories now — but flips
              the whole section to dark (matches the Network scroll above)
              so the homepage below /network reads as one continuous, legit
              platform surface with the featured story on the left and the
              latest headlines column on the right. Only FLYT hides.
              iter · Athletes (hideDiscovery) keep THIS block — the Stories
              swipe hero + Top Stories list — even though every other
              discovery row stays hidden, per Jay. It sits between the
              Launch Pad/Breaking ticker and the Dreams League network hero. */}
      {!isFlyt && (
      <section className={`py-6 lg:py-8 border-b ${compact ? 'border-white/5 bg-black text-white' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7">
              {loading ? (
                <div className="bg-gray-200 rounded-lg h-[500px] skeleton"></div>
              ) : heroFeature ? (
                <div
                  className="relative select-none"
                  data-swipe-ignore="true"
                  onTouchStart={(e) => {
                    if (rotationStories.length < 2) return;
                    const t = e.touches[0];
                    e.currentTarget.dataset.tx = t.clientX;
                    e.currentTarget.dataset.ty = t.clientY;
                  }}
                  onTouchEnd={(e) => {
                    if (rotationStories.length < 2) return;
                    const startX = parseFloat(e.currentTarget.dataset.tx || '0');
                    const startY = parseFloat(e.currentTarget.dataset.ty || '0');
                    const end = e.changedTouches[0];
                    const dx = end.clientX - startX;
                    const dy = end.clientY - startY;
                    // Only treat as a swipe when horizontal motion clearly
                    // beats vertical — keeps page scroll intact. A tap
                    // navigates normally via the underlying <Link>.
                    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4) {
                      e.preventDefault();
                      const dir = dx < 0 ? 1 : -1;
                      setRotationIndex((prev) => (prev + dir + rotationStories.length) % rotationStories.length);
                    }
                  }}
                  data-testid="hero-rotation-swipe-area"
                >
                  <Link key={heroFeature.id} to={`${channel.storiesPath}/${heroFeature.slug}`} onMouseEnter={() => prefetchStory(heroFeature.slug)} onTouchStart={() => prefetchStory(heroFeature.slug)} onClick={() => { try { import('../lib/nativeBridge').then((m) => m.haptic('light')).catch(() => {}); } catch (_e) { /* noop */ } }} className="block group hero-fade-in" data-testid="hero-featured-story">
                    <div className="overflow-hidden rounded-lg mb-5">
                      <img
                        src={resolveImageUrl(heroFeature.cover_image || heroFeature.thumbnail, 1200)}
                        alt={heroFeature.name}
                        fetchPriority="high"
                        loading="eager"
                        decoding="async"
                        className="w-full h-[400px] lg:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <StatusBadge story={heroFeature} size="lg" />
                      <span className={`text-xs font-bold uppercase tracking-wider ${compact ? 'text-blue-300' : 'text-gray-500'}`}>{heroFeature.category}</span>
                    </div>
                    <h2 className={`font-display text-4xl lg:text-5xl mt-2 mb-3 uppercase leading-[1.05] transition ${compact ? 'text-white group-hover:text-blue-200' : 'group-hover:text-gray-700'}`}>
                      {heroFeature.name}
                    </h2>
                    {heroFeature.subtitle && (
                      <p className={`font-italic text-lg mb-3 ${compact ? 'text-gray-300' : 'text-gray-600'}`}>{heroFeature.subtitle}</p>
                    )}
                    <p className={`font-body text-base mb-3 line-clamp-2 ${compact ? 'text-gray-300' : 'text-gray-700'}`}>{heroFeature.summary}</p>
                    <p className={`font-body text-sm ${compact ? 'text-gray-400' : 'text-gray-500'}`}>
                      By <strong>{heroFeature.author}</strong> · {formatDate(heroFeature.publish_date)}
                    </p>
                  </Link>
                  {/* Save overlay on the hero image, top-right of the image only. */}
                  <div className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()}>
                    <SaveButton type="stories" id={heroFeature.id} variant="icon" />
                  </div>
                  {/* Rotation indicators */}
                  {rotationStories.length > 1 && (
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-[42%] sm:bottom-auto sm:top-3 sm:left-3 sm:translate-x-0 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md" data-testid="hero-rotation-dots">
                      {rotationStories.map((s, i) => (
                        <button
                          key={s.id}
                          type="button"
                          aria-label={`Show featured story ${i + 1}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRotationIndex(i); }}
                          className={`h-1.5 rounded-full transition-all ${i === rotationIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/45 hover:bg-white/70'}`}
                          data-testid={`hero-rotation-dot-${i}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Mobile swipe hint — tiny "swipe" pill that fades in
                      once then disappears. Only renders on small screens
                      and only when there's more than one story to swipe
                      through. */}
                  {rotationStories.length > 1 && (
                    <div className="sm:hidden absolute top-3 left-3 z-10 pointer-events-none">
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur-md text-white text-[10px] uppercase tracking-widest font-bold hero-swipe-hint"
                        data-testid="hero-swipe-hint"
                      >
                        ← Swipe →
                      </span>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Top Stories column — now visible in compact mode too, styled dark. */}
            <div className={`lg:col-span-5 lg:pl-12 ${compact ? 'lg:border-l lg:border-white/10' : 'lg:border-l lg:border-gray-200'}`}>
              <h3 className={`font-display text-sm uppercase tracking-wider mb-6 pb-3 border-b ${compact ? 'text-white/50 border-white/10' : 'text-gray-500 border-gray-200'}`}>
                Top Stories
              </h3>
              <div className={compact ? 'divide-y divide-white/10' : 'divide-y divide-gray-200'}>
                {loading ? (
                  Array(4).fill(0).map((_, idx) => (
                    <div key={`side-skeleton-${idx}`} className="py-5">
                      <div className={`h-4 rounded skeleton mb-2 ${compact ? 'bg-white/10' : 'bg-gray-200'}`}></div>
                      <div className={`h-3 rounded skeleton w-2/3 ${compact ? 'bg-white/10' : 'bg-gray-200'}`}></div>
                    </div>
                  ))
                ) : (
                  sideStories.map((story, idx) => (
                    <Link
                      key={story.id}
                      to={`${channel.storiesPath}/${story.slug}`}
                      onMouseEnter={() => prefetchStory(story.slug)}
                      onTouchStart={() => prefetchStory(story.slug)}
                      className="block py-5 group first:pt-0"
                      data-testid={`side-story-${idx}`}
                    >
                      <div className="flex gap-4 items-start">
                        <div className="flex-1 min-w-0">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${compact ? 'text-blue-300' : 'text-gray-500'}`}>{story.category}</span>
                          <h4 className={`font-body font-bold text-base leading-snug mt-1 mb-1 transition line-clamp-3 ${compact ? 'text-white group-hover:text-blue-200' : 'group-hover:text-gray-600'}`}>
                            {story.name}
                          </h4>
                          <p className={`text-xs ${compact ? 'text-white/50' : 'text-gray-500'}`}>
                            {story.author} · {formatDate(story.publish_date)}
                          </p>
                        </div>
                        <img loading="lazy" decoding="async"
                          src={resolveImageUrl(story.thumbnail, 400)}
                          alt={story.name}
                          className="w-24 h-24 object-cover rounded flex-shrink-0"
                        />
                      </div>
                    </Link>
                  ))
                )}
              </div>
              <div className="mt-6">
                {!isFlyt && (
                  <Link to={channel.storiesPath} className={`font-body font-bold text-sm ${compact ? 'text-blue-300 hover:text-blue-200' : 'hover-gradient'}`}>
                    See all stories →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* FLYT-only top-of-page tagline hero + Built Stories grid +
          Featured Films carousel — the very first thing brands/networks
          see when they land on /flyt. */}
      {isFlyt && <FlytTaglineHero />}

      {/* 3.5 — Personalized rows (logged-in) + Dynamic rows + Coming Soon + Collections — Dreams League */}
      {!isFlyt && !isVisionary && !hideDiscovery && (
        <PersonalRows compact={compact} />
      )}
      {/* iter219 · Signed-out visitors don't see the Trending row —
          the `PublicHomeLandingBlock` on `/` already carries the
          "here's what Flythouse is" weight. Signed-in users still get
          the full personalized rail. */}
      {!isFlyt && !isVisionary && !isLoggedOut && !hideDiscovery && (
        <>
          <HorizontalRow
            kicker="What everyone is reading"
            title="Trending Now"
            testid="row-trending"
            badgeKind="trending"
            params={{ sort: 'trending', channel: 'dreams_league', limit: 12 }}
            storiesPath={channel.storiesPath}
          />
        </>
      )}
      {/* iter233 · "Get Access" network hero — injected by NetworkPage
          for signed-in visitors on `/` so it lands right below Continue
          Watching + Trending, per Jay. Null everywhere else. */}
      {networkHeroSlot}
      {/* Coming Soon moved to /search empty state (Feb 2026) — homepage
          now flows straight into the audience switcher. Visionary flow
          still shows it inline because the Visionary /search is thinner. */}
      {isVisionary && <ComingSoonSection channel="dreams_visionary" accent="white" dark />}
      {/* FLYT's ComingSoonSection moved to the bottom of the FLYT flow
          (after Studios teaser) so the first thing visitors see is
          stories, films, and the network — not a teaser card. */}
      {/* Collections row removed — feature not set up yet. */}

      {/* iter229q · "Not highlights, not clickbait" section removed
          entirely per Jay — the persona picker in `PublicHomeLandingBlock`
          already carries the signed-out marketing framing, and Jay felt
          the sub-hero read as filler on every page. */}

      {/* 4. FEATURED REEL (FLYT only) / AUDIENCE CARDS (Dreamers only — Dreams League uses the larger AudienceHero 2x2 below) */}
      {isFlyt ? (
        <>
          {/* FLYT FLOW per user spec:
              1. Tagline hero (rendered above)
              2. FLYT Built Stories grid                ← athletes first
              3. Featured FLYT Films carousel           ← slide to scan
              4. FlytStudiosTeaser (Who we build with + Process)
              5. ComingSoonSection + Featured Reel       ← supporting */}
          <CaseStudiesGrid limit={6} />
          <FlytFilmsCarousel />
        </>
      ) : isVisionary ? (
        <section className="py-20 bg-white" data-testid="audience-cards-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <p className={`font-italic text-sm mb-3 tracking-wider uppercase ${isVisionary ? 'text-black' : 'text-blue-500'}`}>
                {isVisionary ? 'Every voice. Every vision.' : 'Built for everyone in the game'}
              </p>
              <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight">
                {isVisionary ? (
                  <>One platform. <span className="text-black">Every visionary.</span></>
                ) : (
                  <>One platform. <span className="blue-gradient">Four playbooks.</span></>
                )}
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-6">
              {audienceCards.map((c) => {
                const Icon = c.icon;
                return (
                  <Link
                    key={c.title}
                    to={c.to}
                    className="group relative bg-white border-2 border-gray-200 hover:border-black rounded-2xl p-4 sm:p-7 transition-all hover:-translate-y-1"
                    data-testid={c.testid}
                  >
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black text-white flex items-center justify-center mb-3 sm:mb-5 transition ${isVisionary ? 'group-hover:bg-gray-700' : 'group-hover:bg-blue-500'}`}>
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <h3 className="font-display text-base sm:text-xl mb-2 sm:mb-3 uppercase leading-tight">{c.title}</h3>
                    <p className="font-body text-gray-600 text-xs sm:text-sm mb-3 sm:mb-5 leading-snug sm:leading-relaxed line-clamp-3 sm:line-clamp-none">{c.body}</p>
                    <span className={`inline-flex items-center gap-1 font-body font-bold text-xs sm:text-sm transition ${isVisionary ? 'group-hover:text-black' : 'group-hover:text-blue-600'}`}>
                      {c.cta} <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-0.5 transition" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* 5. AUDIENCE-SWITCHING HERO — moved above the "Why Dreams
          League" section (search for "playbooks" comment ~line 387).
          Renders once per homepage, no duplicate here. */}

      {/* 6. LATEST STORIES GRID (Dreams League only — FLYT jumps straight to Case Studies) */}
      {!isFlyt && (
      <section className={`py-12 ${compact ? 'bg-black' : 'bg-[#f7f6f3]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <h2 className={`font-display text-3xl sm:text-4xl uppercase ${compact ? 'text-white' : ''}`} data-testid="latest-stories-heading">Latest Access</h2>
            <Link to={channel.storiesPath} className={`hidden md:inline font-body font-bold text-sm ${compact ? 'text-blue-300 hover:text-white' : 'hover-gradient'}`}>View all →</Link>
          </div>
          {/* List view — thin rows: thumbnail left, title + subheading right.
              Saves vertical space vs. big squares and reads cleaner. */}
          <div className="flex flex-col gap-2 sm:gap-3">
            {loading ? (
              Array(6).fill(0).map((_, idx) => (
                <div key={`latest-skeleton-${idx}`} className={`rounded-xl skeleton h-20 sm:h-24 ${compact ? 'bg-white/5' : 'bg-white'}`}></div>
              ))
            ) : (
              tailoredLatest.slice(0, 8).map((story, idx) => (
                <div key={story.id} className="relative">
                  <Link
                    to={`${channel.storiesPath}/${story.slug}`}
                    onMouseEnter={() => prefetchStory(story.slug)}
                    onTouchStart={() => prefetchStory(story.slug)}
                    className={`group flex items-stretch gap-3 rounded-xl overflow-hidden ring-1 transition ${compact ? 'ring-white/10 bg-white/[0.04] hover:bg-white/[0.09]' : 'ring-black/5 bg-white hover:shadow-md'}`}
                    data-testid={`latest-story-${idx}`}
                  >
                    <img src={resolveImageUrl(story.thumbnail, 260)} alt={story.name} loading="lazy" decoding="async" className="w-24 sm:w-32 h-20 sm:h-24 object-cover flex-shrink-0" />
                    <div className="min-w-0 flex-1 py-2 pr-3 flex flex-col justify-center">
                      <span className={`text-[10px] uppercase tracking-widest font-bold mb-0.5 ${compact ? 'text-blue-300' : 'text-blue-600'}`}>{story.category}</span>
                      <h3 className={`font-body font-bold text-sm sm:text-base leading-tight line-clamp-1 ${compact ? 'text-white' : ''}`}>{story.name}</h3>
                      <p className={`text-xs leading-snug line-clamp-1 mt-0.5 ${compact ? 'text-white/60' : 'text-gray-600'}`}>{story.summary}</p>
                      <p className={`text-[10px] mt-1 ${compact ? 'text-white/40' : 'text-gray-500'}`}>{formatDate(story.publish_date)}</p>
                    </div>
                  </Link>
                  <CardSaveOverlay type="stories" id={story.id} />
                </div>
              ))
            )}
          </div>
          <div className="text-center mt-8">
            <Link to={channel.storiesPath} className={`btn-pill inline-block ${compact ? 'bg-white text-black' : 'bg-black text-white'}`} data-testid="view-all-stories-btn">
              View All Stories
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* FLYT-ONLY — Studios teaser (Who we build with + Process) */}
      {isFlyt && <FlytStudiosTeaser />}

      {/* FLYT-ONLY — Featured Reel + Coming Soon footer-style cards */}
      {isFlyt && <FeaturedReel channel={channel.id} accentClass={accent} isDark={isFlyt} />}
      {isFlyt && <ComingSoonSection channel="flyt" accent="gold" dark />}

      {/* 6. AD SLOT #1 (leaderboard) — hidden on Visionaries (cleaner editorial feel) */}
      {!isVisionary && <AdSlot id="1" label="Featured Partner Slot" format="970 × 250" position="leaderboard" />}

      {/* 8. ATHLETE SPOTLIGHT — Dreams League only */}
      {!isFlyt && !isVisionary && !hideDiscovery && <AthleteSpotlight />}

      {/* 9. IMPACT STRIP — removed per founder request (Feb 2026). The
          "Why Dreams League" section above already carries the topline
          stats (4.5M+ views, 15+ pro athletes, Stories From logos). */}

      {/* 10. AD SLOT #2 (in-article rect) — hidden on Visionaries.
              Also hidden on FLYT because Ad Slot #1 already sits a few
              hundred pixels above with only the stat strip between them —
              two ads back-to-back felt crowded on the FLYT page. */}
      {!isVisionary && !isFlyt && <AdSlot id="2" label="Sponsored Slot" format="728 × 200" position="in-feed" />}

      {/* 11. JAY QUOTE / ABOUT — removed per founder request (Feb 2026).
              The About page still owns Jay's full bio + quote for anyone
              who wants the story. Homepage now flows straight from the
              stat strip into the newsletter signup. */}

      {/* iter55 · BUILT FOR ATHLETES — FLYT ID™ outcome section. Only
          shown on Dreams League (not FLYT or Visionary) — it's an athlete
          product pitch, not relevant on the industry-facing FLYT feed.
          iter219 · Hidden for signed-out visitors — the new
          `PublicHomeLandingBlock` at the top of `/` already carries
          the "Your career deserves more than a link" pitch as its H1
          headline.
          iter230 · Now ALSO hidden for signed-in users per Jay — the
          signed-in home should be a personal feed, not a marketing
          pitch. The section is effectively retired from Homepage; the
          equivalent messaging lives inside `PublicHomeLandingBlock`
          for signed-out visitors. */}
      {false && !isFlyt && !isVisionary && !isLoggedOut && (
        <section className="py-14 sm:py-20 bg-neutral-950" data-testid="homepage-flyt-link-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-blue-300 font-black mb-2">Built for athletes</p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.98] text-white">
                Your career deserves more than a link list.
              </h2>
              <p className="mt-4 text-white/70 text-base sm:text-lg leading-relaxed max-w-lg">
                Build one verified page for your story, highlights, stats, recruiting information, social links, and opportunities.
              </p>
              <div className="mt-4 inline-block px-2.5 py-1 rounded-full ring-1 ring-white/15 bg-white/[0.04] text-white text-[11px] uppercase tracking-widest font-bold">
                $4.99/month · Cancel anytime
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Link
                  to="/billing?intent=flyt-link"
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
                  data-testid="homepage-flyt-link-build"
                >
                  Build your profile
                </Link>
                <Link
                  to="/jaylen-carter"
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full ring-1 ring-white/20 bg-white/5 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-white/15 transition"
                  data-testid="homepage-flyt-link-example"
                >
                  See athlete example
                </Link>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                {[
                  { t: 'Be Found',        b: 'Verified profile + searchable FLYT presence.' },
                  { t: 'Tell Your Story', b: 'Bio, milestones, stories, photos, videos.' },
                  { t: 'Share Everything',b: 'One link for Hudl, stats, recruiting, socials.' },
                  { t: 'See Who Cares',   b: 'Profile views, engagement, link clicks, requests.' },
                ].map((o) => (
                  <div key={o.t} className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-3">
                    <p className="font-display text-xs uppercase tracking-widest text-white">{o.t}</p>
                    <p className="text-[11px] text-white/60 mt-1 leading-snug">{o.b}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-6 bg-blue-500/10 blur-3xl rounded-full pointer-events-none" aria-hidden />
              <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 bg-gradient-to-br from-black to-neutral-900 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full ring-2 ring-blue-400/40 bg-white/5" />
                  <div>
                    <p className="text-white font-bold text-sm inline-flex items-center gap-1.5">
                      Your Athlete Name
                      <span className="inline-block w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-black leading-4 text-center">✓</span>
                    </p>
                    <p className="text-white/50 text-[11px]">flythouse.com/l/your-slug</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {['Highlights', 'Season Stats', 'Recruiting', 'Stories'].map((s) => (
                    <div key={s} className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-3">
                      <p className="text-[9px] uppercase tracking-widest text-white/40 font-bold">Card</p>
                      <p className="text-white text-xs font-bold mt-0.5">{s}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-3 py-2 flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Business inquiries</p>
                  <span className="text-[10px] text-blue-300 font-bold">Contact →</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 12. NEWSLETTER */}
      {!hideDiscovery && <NewsletterSignup />}

      {/* 13. FINAL CTA — iter233 · "YOUR STORY STARTS NOW" removed
          entirely per Jay. Visionary channel keeps its "VISION" variant. */}
      {isVisionary && (
      <section className="bg-black text-white py-24 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-display text-5xl lg:text-7xl mb-6 uppercase">
            YOUR <span className="text-white underline decoration-2 underline-offset-4">VISION</span> STARTS NOW
          </h2>
          <p className="font-italic text-2xl text-gray-300 mb-10">
            Don't wait for someone to write your story. Let's tell it the way it actually went.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/visionary/contact"
              className="btn-pill bg-white text-black inline-block"
              data-testid="final-apply-btn"
            >
              Tell Your Story
            </Link>
            <Link
              to="/visionary/network"
              className="btn-pill border-2 border-white text-white inline-block"
              data-testid="final-contact-btn"
            >
              Explore Visionaries
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* iter219 · Breaking News bar for signed-out visitors — moved
          here at the bottom so it isn't the first impression, but
          still visible to anyone scrolling the full page. Signed-in
          users saw it at the top already, so this is a no-op for them. */}
      {breakingStories.length > 0 && isLoggedOut && (
        <div className={`py-3 border-t ${compact ? 'bg-black border-white/10' : 'bg-white border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide">
              <span className={`font-display text-xs uppercase whitespace-nowrap tracking-wider ${compact ? 'text-red-500' : 'text-red-600'}`}>● Breaking</span>
              {breakingStories.map((story, idx) => (
                <Link
                  key={story.id}
                  to={`${channel.storiesPath}/${story.slug}`}
                  onMouseEnter={() => prefetchStory(story.slug)}
                  onTouchStart={() => prefetchStory(story.slug)}
                  className={`font-body text-sm whitespace-nowrap transition ${compact ? 'text-white hover:text-blue-300' : 'hover-gradient'}`}
                  data-testid={`breaking-story-bottom-${idx}`}
                >
                  {story.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;
